import React, {
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((message, opts = {}) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      message,
      tone: opts.tone || 'success', // success | error | info
      duration: opts.duration ?? 1800,
    };
    setToasts((prev) => [...prev, toast]);
    if (toast.duration > 0) {
      setTimeout(() => dismiss(id), toast.duration);
    }
    return id;
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ push, dismiss }}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="fixed top-5 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-2 pointer-events-none"
      >
        {toasts.map((t) => {
          const palette =
            t.tone === 'error'
              ? 'bg-red-600 text-white border-black'
              : t.tone === 'info'
              ? 'bg-white text-black border-black'
              : 'bg-yellow-400 text-black border-black';
          return (
            <div
              key={t.id}
              role="status"
              className={`pointer-events-auto px-6 py-3 font-black uppercase tracking-wider border-4 ${palette} shadow-[6px_6px_0px_rgba(0,0,0,1)] animate-in slide-in-from-top duration-200`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xl">★</span>
                <span>{t.message}</span>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return { push: () => {}, dismiss: () => {} };
  }
  return ctx;
};
